import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import type { ApiClient } from '../api/client';
import { reportError } from '../utils/errors';

export const DEFAULT_TERMINAL_FONT_SIZE = 13;
export const MIN_TERMINAL_FONT_SIZE = 10;
export const MAX_TERMINAL_FONT_SIZE = 18;

type TerminalFontSizeContextValue = {
  fontSize: number;
  saving: boolean;
  saveFontSize: (fontSize: number | null) => Promise<boolean>;
};

const TerminalFontSizeContext = createContext<TerminalFontSizeContextValue>({
  fontSize: DEFAULT_TERMINAL_FONT_SIZE,
  saving: false,
  saveFontSize: async () => false
});

function normalizeTerminalFontSize(value: number | null | undefined) {
  if (
    typeof value !== 'number' ||
    !Number.isInteger(value) ||
    value < MIN_TERMINAL_FONT_SIZE ||
    value > MAX_TERMINAL_FONT_SIZE
  ) {
    return DEFAULT_TERMINAL_FONT_SIZE;
  }
  return value;
}

type TerminalFontSizeProviderProps = {
  apiClient: ApiClient;
  userId?: string | null;
  initialFontSize?: number | null;
  setError: (message: string | null) => void;
  children: ReactNode;
};

export function TerminalFontSizeProvider({
  apiClient,
  userId,
  initialFontSize,
  setError,
  children
}: TerminalFontSizeProviderProps) {
  const normalizedInitialFontSize = normalizeTerminalFontSize(initialFontSize);
  const [fontSize, setFontSize] = useState(normalizedInitialFontSize);
  const [saving, setSaving] = useState(false);
  const previousUserIdRef = useRef(userId);

  useEffect(() => {
    if (previousUserIdRef.current === userId) return;
    previousUserIdRef.current = userId;
    setFontSize(normalizedInitialFontSize);
  }, [normalizedInitialFontSize, userId]);

  const value = useMemo<TerminalFontSizeContextValue>(() => ({
    fontSize,
    saving,
    saveFontSize: async (requestedFontSize) => {
      const nextFontSize = normalizeTerminalFontSize(requestedFontSize);
      const storedFontSize = requestedFontSize === null ? null : nextFontSize;
      setSaving(true);
      try {
        await apiClient.patch('/api/me/terminal-preferences', {
          terminal_font_size: storedFontSize
        });
        setFontSize(nextFontSize);
        return true;
      } catch (err) {
        reportError('Failed to save terminal text size', err, setError);
        return false;
      } finally {
        setSaving(false);
      }
    }
  }), [apiClient, fontSize, saving, setError]);

  return <TerminalFontSizeContext.Provider value={value}>{children}</TerminalFontSizeContext.Provider>;
}

export function useTerminalFontSize() {
  return useContext(TerminalFontSizeContext);
}

export function TerminalFontSizeControl() {
  const { fontSize, saving, saveFontSize } = useTerminalFontSize();
  const [draftFontSize, setDraftFontSize] = useState(fontSize);
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const rangeId = useId();

  const apply = async () => {
    if (await saveFontSize(draftFontSize)) {
      detailsRef.current?.removeAttribute('open');
    }
  };
  const reset = async () => {
    if (await saveFontSize(null)) {
      detailsRef.current?.removeAttribute('open');
    }
  };

  return (
    <details ref={detailsRef} className="terminal-panel__font-size">
      <summary
        aria-label={`Terminal text size, ${fontSize} pixels`}
        title="Terminal text size"
        onClick={() => setDraftFontSize(fontSize)}
      >
        <span aria-hidden="true">Aa</span>
        <span className="terminal-panel__font-size-value">{fontSize}</span>
      </summary>
      <div className="terminal-panel__font-size-popover">
        <label htmlFor={rangeId}>Terminal text size</label>
        <div className="terminal-panel__font-size-range-row">
          <input
            id={rangeId}
            type="range"
            min={MIN_TERMINAL_FONT_SIZE}
            max={MAX_TERMINAL_FONT_SIZE}
            step="1"
            value={draftFontSize}
            onChange={(event) => setDraftFontSize(Number(event.target.value))}
            disabled={saving}
          />
          <output htmlFor={rangeId}>{draftFontSize}px</output>
        </div>
        <div className="terminal-panel__font-size-actions">
          <button type="button" onClick={() => void apply()} disabled={saving}>
            {saving ? 'Saving...' : 'Apply'}
          </button>
          <button type="button" className="ghost" onClick={() => void reset()} disabled={saving}>
            Reset default
          </button>
        </div>
      </div>
    </details>
  );
}
